import type { AuthV1 } from '@alpod-ai-writer/backend/src/schemas/auth/auth.schema';
import * as Sentry from '@sentry/react';
import type Elysia from 'elysia';
import {createParser, type EventSourceMessage} from 'eventsource-parser';

import { ApiError } from './apiError';

const REFRESH_TOKENS_BEFORE_EXPIRATION_MS = 60 * 1000 * 5; // 5 minutes
const MAX_REFRESH_RETRIES = 3;
const SESSION_STORAGE_KEY = '_session';

export interface ApiClientOptions {
    baseUrl: string;
}

export interface EstablishSessionOptions {
    /**
     * Callback to revoke the old session on the backend (if one exists).
     * Should return a promise that resolves when the session is revoked.
     * Errors are caught and ignored (session might already be invalid).
     */
    revokeOldSession?: (sessionId: string) => Promise<void>;
}

type ErrorBody = {
    error: string,
    message: string,
    details?: unknown,
    reqId?: string,
    traceId?: string
};

interface RawRequestOptions {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
    headers?: Record<string, string>;
    transformResponse?: (response: any) => unknown;
}

export interface RequestOptions<Response> {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    transformResponse?: (response: Response) => Response;
};

export interface RequestOptionsWithBody<Body, Response> extends RequestOptions<Response> {
    body: Body;
}

export class ApiClient<Path extends string = string> extends EventTarget {
    private refreshPromise: Promise<AuthV1.Tokens|null>|null = null;
    private _needToRefreshTokens: boolean = false;
    private _boundStorageHandler: (e: StorageEvent) => void;
    
    /** In-memory session storage - primary source of truth */
    private _session: AuthV1.Tokens | null = null;

    public constructor(public readonly opts: ApiClientOptions) {
        super();

        // Load session from localStorage on init (if available)
        this._session = this.loadSessionFromStorage();

        // Listen for storage changes from other tabs to sync session state
        this._boundStorageHandler = this.handleStorageChange.bind(this);
        window.addEventListener('storage', this._boundStorageHandler);
    }

    /**
     * Clean up event listeners when the client is no longer needed.
     */
    public destroy(): void {
        window.removeEventListener('storage', this._boundStorageHandler);
    }

    private loadSessionFromStorage(): AuthV1.Tokens | null {
        try {
            const session = localStorage?.getItem(SESSION_STORAGE_KEY);

            if (!session) {
                return null;
            }

            const sessionData = JSON.parse(session);

            return {
                ...sessionData,
                accessTokenExpiresAt: new Date(sessionData.accessTokenExpiresAt)
            };
        } catch {
            return null;
        }
    }

    private saveSessionToStorage(session: AuthV1.Tokens): void {
        try {
            localStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
        } catch {
            // Storage unavailable or quota exceeded
        }
    }

    private removeSessionFromStorage(): void {
        try {
            localStorage?.removeItem(SESSION_STORAGE_KEY);
        } catch {
            // Storage unavailable
        }
    }

    /**
     * Handle storage changes from other tabs to keep session state in sync.
     */
    private handleStorageChange(e: StorageEvent): void {
        if (e.key === SESSION_STORAGE_KEY) {
            const oldSession = this._session;
            const newSession = this.loadSessionFromStorage();
            this._session = newSession;

            if (oldSession && !newSession) {
                // Session was cleared in another tab
                console.debug('Session cleared in another tab.');
                this.dispatchEvent(new Event('sessionExpired'));
            } else if (!oldSession && newSession) {
                // New session was established in another tab
                console.debug('Session established in another tab.');
                this._needToRefreshTokens = false; // Reset since we have fresh tokens
                this.dispatchEvent(new Event('sessionEstablished'));
            } else if (oldSession && newSession && oldSession.identityId !== newSession.identityId) {
                // User changed in another tab (e.g., logged in as different user)
                console.debug('User changed in another tab, new identityId:', newSession.identityId);
                this._needToRefreshTokens = false; // Reset since we have fresh tokens
                this.dispatchEvent(new Event('sessionEstablished'));
            } else if (oldSession && newSession) {
                // Session was updated in another tab (e.g., token refresh) - same user
                console.debug('Session tokens refreshed in another tab.');
                this._needToRefreshTokens = false; // Reset since we have fresh tokens
                this.dispatchEvent(new Event('tokenUpdated'));
            }
        }
    }

    /**
     * Store session in memory and sync to localStorage.
     * This is an internal method used for token refresh - does not emit events.
     * Use establishSession() for establishing new sessions.
     */
    private storeSession(session: AuthV1.Tokens): void {
        this._session = session;
        this.saveSessionToStorage(session);
    }

    /**
     * Establish a new session, optionally replacing an existing one.
     * This method handles:
     * 1. Revoking the old session on the backend (if exists and callback provided)
     * 2. Clearing the local session to notify other tabs (if exists)
     * 3. Storing the new session
     * 4. Emitting sessionEstablished event
     */
    public async establishSession(newSession: AuthV1.Tokens, options?: EstablishSessionOptions): Promise<void> {
        const oldSessionId = this.getSessionId();

        // Revoke old session on backend if exists and callback provided
        if (oldSessionId && options?.revokeOldSession) {
            try {
                console.debug('Revoking old session on backend:', oldSessionId);
                await options.revokeOldSession(oldSessionId);
                console.debug('Old session revoked successfully.');
            } catch (err) {
                // Ignore errors - session might already be invalid or revoked
                console.debug('Failed to revoke old session (ignoring):', err);
            }
        }

        // Store the new session (in memory + localStorage)
        // Other tabs will detect user change via handleStorageChange comparing identityId
        this._session = newSession;
        this.saveSessionToStorage(newSession);
        this._needToRefreshTokens = false;

        // Dispatch events
        this.dispatchEvent(new Event('sessionEstablished'));

        console.debug('Session established successfully.');
    }

    /**
     * Get current session from memory.
     */
    private getSession(): AuthV1.Tokens | null {
        return this._session;
    }

    private clearSession(): void {
        console.trace('Clearing session.');
        this._session = null;
        this.removeSessionFromStorage();
    }

    public getIdentityId(): string | null {
        const session = this.getSession();
        return session?.identityId ?? null;
    }

    public getSessionId(): string | null {
        const session = this.getSession();
        return session?.sessionId ?? null;
    }

    public getAccessToken(): string | null {
        const session = this.getSession();
        return session?.accessToken ?? null;
    }

    public getAccessTokenExpireDate(): Date | null {
        const session = this.getSession();
        return session?.accessTokenExpiresAt ?? null;
    }

    public hasSession(): boolean {
        return this._session !== null;
    }

    public deleteSession(): void {
        this.clearSession();
        this.dispatchEvent(new Event('sessionDeleted'));
    }

    private getRequestParams(path: string, opts: RawRequestOptions) {
        let _path = path;
        let _url: URL;
        let _body: FormData|string|undefined;
        const _headers = opts.headers ?? {};
        
        if (opts.params) {
            for (const [key, value] of Object.entries(opts.params)) {
                _path = _path.replace(`:${key}`, String(value));
            }
        }

        try {
            _url = new URL(this.opts.baseUrl + _path, !this.opts.baseUrl.startsWith('http') ? window.location.origin : undefined);
        } catch (err) {
            console.error('Failed to parse URL:', this.opts.baseUrl + path, err);
            throw err;
        }

        if (opts.query) {
            for (const [key, value] of Object.entries(opts.query)) {
                let _value: string;

                if (value instanceof Date) {
                    _value = value.toISOString();
                } else if (value instanceof Object) {
                    _value = JSON.stringify(value);
                } else {
                    _value = String(value);
                }
                
                _url.searchParams.append(key, _value);
            }
        }

        if (opts.body instanceof FormData) {
            _body = opts.body;
        } else if (opts.body !== undefined && opts.body !== null) {
            _body = JSON.stringify(opts.body);
            _headers['Content-Type'] = 'application/json';
        }

        return {
            url: _url,
            body: _body,
            headers: _headers
        };
    }

    private async makeRequest<Response = unknown>(method: string, path: string, opts: RawRequestOptions): Promise<Response> {
        const { url, body, headers } = this.getRequestParams(path, opts);
        
        const res = await fetch(url, {
            method,
            body,
            headers
        });

        if (!res.ok) {
            let body: unknown;

            try {
                body = await res.json();
            } catch (err) {
                console.error('Failed to parse response body:', err);
            }

            let errorBody: undefined|ErrorBody;

            if (body instanceof Object && 'error' in body) {
                errorBody = body as ErrorBody;
            }

            console.error('API error:', errorBody);

            throw new ApiError(
                res.status,
                errorBody?.error ?? 'UnknownError',
                errorBody?.message ?? 'No message was provided.',
                errorBody?.details,
                errorBody?.reqId,
                errorBody?.traceId
            );
        } else {
            if (res.status !== 204) {
                const data = await res.json();
                return opts.transformResponse ? opts.transformResponse(data) as Response : data;
            } else {
                return null as unknown as Response;
            }
        }
    }

    public async refreshTokens(): Promise<AuthV1.Tokens> {
        console.debug('Refreshing tokens...');
        
        const res = await this.makeRequest<AuthV1.Tokens>('POST', '/v1/auth/token/refreshByCookie', {});
        const session: AuthV1.Tokens = {
            ...res,
            accessTokenExpiresAt: new Date(res.accessTokenExpiresAt)
        };

        this.storeSession(session);
        this._needToRefreshTokens = false;
        this.dispatchEvent(new Event('tokenUpdated'));

        console.debug('Tokens refreshed successfully.');
        return session;
    }

    /**
     * Check if an error is a transient error that should be retried indefinitely.
     * This includes server errors (5xx) and network/offline errors.
     */
    private isTransientError(err: unknown): boolean {
        // Network errors (offline, DNS failure, etc.)
        if (err instanceof TypeError && err.message.includes('fetch')) {
            return true;
        }

        // Server errors (5xx)
        if (err instanceof ApiError && err.status >= 500) {
            return true;
        }

        return false;
    }

    public async tryRefreshTokens(): Promise<AuthV1.Tokens|null> {
        let refreshRetries = 0;

        while (true) {
            try {
                return await this.refreshTokens();
            } catch (err) {
                const isTransient = this.isTransientError(err);

                if (isTransient) {
                    // For transient errors (5xx, offline), retry indefinitely with backoff
                    console.debug('Transient error while refreshing tokens, will retry indefinitely. Reason:', err);
                    await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, refreshRetries), 30000))); // Exponential backoff, max 30s
                    refreshRetries++;
                    continue;
                }

                if (err instanceof ApiError && err.status === 401) {
                    // Unauthorized, probably due to refresh-token racing conditions with another tap - retry up to MAX_REFRESH_RETRIES
                    refreshRetries++;
                    console.debug('Failed to refresh tokens due to unauthorized error, retrying (%d/%d)...', refreshRetries, MAX_REFRESH_RETRIES);
                    
                    if (refreshRetries >= MAX_REFRESH_RETRIES) {
                        break;
                    }

                    await new Promise(resolve => setTimeout(resolve, 1000));
                    continue;
                }

                if (err instanceof ApiError && (err.status === 422 || err.status === 400 || err.status === 403)) {
                    // Unrecoverable client errors - don't retry
                    // 422: Validation error (refresh token cookie expired/missing)
                    // 400: Bad request
                    // 403: Forbidden (session revoked)
                    console.debug('Unrecoverable error while refreshing tokens (status %d), giving up.', err.status);
                    break;
                }

                // Unknown error - don't retry
                console.error('Unknown error while refreshing tokens, giving up. Reason:', err);
                break;
            }
        }

        const session = this.getSession();

        Sentry.captureMessage('Failed to refresh tokens', {
            extra: {
                sessionId: session?.sessionId,
                identityId: session?.identityId,
            },
            level: 'debug',
            tags: {
                'category': 'auth'
            }
        });

        this.clearSession();
        this.dispatchEvent(new Event('sessionExpired'));

        console.debug('Session expired.');
        throw new Error('Session expired.');
    }

    private async makeRequestWithTokens<Response = unknown>(method: string, path: string, opts: RawRequestOptions): Promise<Response> {
        let session = this.getSession();

        if (session) {
            // Refresh token if it's expired
            // Store the refresh promise in case there are multiple racing requests
            const tokenExpiresInMs = session.accessTokenExpiresAt.getTime() - Date.now();
            console.debug('Token expires in %d ms', tokenExpiresInMs, tokenExpiresInMs < REFRESH_TOKENS_BEFORE_EXPIRATION_MS ? 'will refresh' : 'will not refresh');

            if (tokenExpiresInMs < REFRESH_TOKENS_BEFORE_EXPIRATION_MS || this._needToRefreshTokens) {
                if (this.refreshPromise) {
                    session = await this.refreshPromise;
                } else {
                    try {
                        this.refreshPromise = this.tryRefreshTokens();
                        session = await this.refreshPromise;
                    } finally {
                        this.refreshPromise = null;
                    }
                }
            }
        }

        try {
            return await this.makeRequest<Response>(method, path, {
                ...opts,
                headers: {
                    ...opts.headers,
                    authorization: session ? `Bearer ${session.accessToken}` : ''
                }
            });
        } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                this._needToRefreshTokens = true;
                console.debug('Got unauthorized, will try to refresh token next time.');
            }

            throw err;
        }
    }

    private async *makeEventStreamWithTokens<Body, Event>(method: string, path: string, opts: RequestOptionsWithBody<Body, undefined>): AsyncGenerator<Event> {
        let session = this.getSession();

        if (session) {
            // Refresh token if it's expired
            // Store the refresh promise in case there are multiple racing requests
            const tokenExpiresInMs = session.accessTokenExpiresAt.getTime() - Date.now();
            console.debug('Token expires in %d ms', tokenExpiresInMs, tokenExpiresInMs < REFRESH_TOKENS_BEFORE_EXPIRATION_MS ? 'will refresh' : 'will not refresh');

            if (tokenExpiresInMs < REFRESH_TOKENS_BEFORE_EXPIRATION_MS || this._needToRefreshTokens) {
                if (this.refreshPromise) {
                    session = await this.refreshPromise;
                } else {
                    try {
                        this.refreshPromise = this.tryRefreshTokens();
                        session = await this.refreshPromise;
                    } finally {
                        this.refreshPromise = null;
                    }
                }
            }
        }

        try {
            const { url, body, headers } = this.getRequestParams(path, opts);

            const res = await fetch(url, {
                method,
                body,
                headers: {
                    ...headers,
                    authorization: session ? `Bearer ${session.accessToken}` : ''
                }
            });

            if (!res.body) {
                throw new Error('Response body is not available.');
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder('utf-8');

            let events: EventSourceMessage[] = [];
            let resolve: (() => void)|null = null;
            let streamClosed = false;

            const parser = createParser({
                onEvent: (event) => {
                    events.push(event);
                    resolve?.();
                    resolve = null;
                }
            });

            while (!streamClosed || events.length > 0) {
                if (events.length >0) {
                    // Process event
                    const event = events.shift();

                    try {
                        if (!event?.data) {
                            continue;
                        }

                        const parsedData = JSON.parse(event.data);

                        if (parsedData.type === 'error') {
                            throw new ApiError(
                                parsedData.status,
                                parsedData.error,
                                parsedData.message,
                                parsedData.details,
                                parsedData.reqId,
                                parsedData.traceId
                            );
                        }

                        yield parsedData as Event;
                    } catch (err) {
                        console.error('Failed to parse event stream data:', err);
                        throw err;
                    }
                } else {
                    // Wait for the next event
                    const { done, value } = await reader.read();

                    if (done) {
                        streamClosed = true;
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });
                    parser.feed(chunk);
                }
            }
        } catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                this._needToRefreshTokens = true;
                console.debug('Got unauthorized, will try to refresh token next time.');
            }

            throw err;
        }
    }

    public async get<Response>(path: Path, opts?: RequestOptions<Response>): Promise<Response>;
    public async get<Response>(path: string, opts?: RequestOptions<Response>): Promise<Response>;
    public async get<Response>(path: Path|string, opts?: RequestOptions<Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('GET', path, {
            ...opts
        });
    }

    public async post<Body, Response>(path: Path, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async post<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async post<Body, Response>(path: Path|string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('POST', path, {
            ...opts
        });
    }

    public async put<Body, Response>(path: Path, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async put<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async put<Body, Response>(path: Path|string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('PUT', path, {
            ...opts
        });
    }

    public async delete<Response>(path: Path, opts: RequestOptions<Response>): Promise<Response>;
    public async delete<Response>(path: string, opts: RequestOptions<Response>): Promise<Response>;
    public async delete<Response>(path: Path|string, opts: RequestOptions<Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('DELETE', path, {
            ...opts
        });
    }

    public async deleteWithBody<Body, Response>(path: Path, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async deleteWithBody<Body, Response>(path: string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response>;
    public async deleteWithBody<Body, Response>(path: Path|string, opts: RequestOptionsWithBody<Body, Response>): Promise<Response> {
        return this.makeRequestWithTokens<Response>('DELETE', path, {
            ...opts
        });
    }

    public async *postEventStream<Body, Event>(path: Path, opts: RequestOptionsWithBody<Body, undefined>): AsyncGenerator<Event> {
        yield* this.makeEventStreamWithTokens<Body, Event>('POST', path, opts);
    }

    public simulateSessionExpiration(): void {
        if (this.hasSession()) {
            this.dispatchEvent(new Event('sessionExpired'));
            this.clearSession();
        }
    }
}

export function createTypedApiClient<App extends Elysia<any, any, any, any, any, any, any>>(opts: ApiClientOptions) {    
    return new ApiClient(opts) as TypedApiClient<App>;
}

type UnionToIntersect<U> = (U extends any ? (arg: U) => any : never) extends (arg: infer I) => void ? I : never;

type AnyTypedRoute = {
    body?: unknown;
    headers?: unknown;
    query?: unknown;
    params?: unknown;
    response: Record<number, unknown>;
};

type TreatyToPath<T, Path extends string = ''> = UnionToIntersect<T extends Record<string, unknown> ? {
    [K in keyof T]: T[K] extends AnyTypedRoute ? {
        [path in Path]: {
            [method in K]: T[K];
        };
    } : unknown extends T[K] ? {
        [path in Path]: {
            [method in K]: T[K];
        };
    } : TreatyToPath<T[K], `${Path}/${K & string}`>;
}[keyof T] : {}>;

export type TypedApiClient<App extends Elysia<any, any, any, any, any, any, any>|undefined = undefined> = ApiClient<App extends {
    _routes: infer Schema extends Record<string, unknown>;
} ? (keyof TreatyToPath<Schema>) : string>;
